import { UPDATE_ITEM_MUTATION } from '@/components/LegacyMiniCart/cartOptions.gql';
import StoreConfig from '@/config';
import { useCartContext } from '@/context/cart';
import { useEventingContext } from '@/context/eventing';
import { useMiniCartInfo } from '@/context/mini-cart';
import { checkCartItemsStock } from '@/lib/get-cart-item-is-out-of-stock';
import { deriveErrorMessage } from '@/util/deriveErrorMessage';
import mergeOperations from '@/util/shallowMerge';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useResolveCartKeyChains } from '../use-resolve-cart-key-chains';
import DEFAULT_OPERATIONS from './miniCart.gql';

export const useMiniCart = props => {
  const { isOpen, setIsOpen } = props;

  const [, { dispatch }] = useEventingContext();

  const operations = mergeOperations(DEFAULT_OPERATIONS, props.operations);
  const { removeItemMutation, miniCartQuery } = operations;

  const [{ cartId }] = useCartContext();
  const router = useRouter();

  const { miniCartData, miniCartDataLoading: miniCartLoading } = useMiniCartInfo();

  const configurableThumbnailSource = StoreConfig.configurable_thumbnail_source;

  const storeUrlSuffix = StoreConfig.product_url_suffix;

  const [removeItem, { loading: removeItemLoading, called: removeItemCalled, error: removeItemError }] =
    useMutation(removeItemMutation);

  const [reqUpdateItemQuantity] = useMutation(UPDATE_ITEM_MUTATION);
  const [updatingUids, setUpdatingUids] = useState([]);

  const handleUpdateItemQuantity = (itemUid, quantity) => {
    setUpdatingUids(x => x.concat(itemUid));
    reqUpdateItemQuantity({
      variables: {
        cartId,
        itemId: itemUid,
        quantity,
      },
    }).finally(() => {
      setUpdatingUids(prev => prev.filter(x => x !== itemUid));
    });
  };

  const totalQuantity = useMemo(() => {
    if (!miniCartLoading) {
      return miniCartData?.cart?.total_quantity;
    }
  }, [miniCartData, miniCartLoading]);

  const subTotal = useMemo(() => {
    if (!miniCartLoading) {
      return miniCartData?.cart?.prices?.subtotal_excluding_tax;
    }
  }, [miniCartData, miniCartLoading]);

  const productList = useMemo(() => {
    if (!miniCartLoading) {
      const temp = [];

      if (miniCartData?.cart?.items) {
        for (let i = miniCartData.cart.items.length - 1; i > -1; i--) {
          temp.push(miniCartData.cart.items[i]);
        }
      }

      return temp;
    }
  }, [miniCartData, miniCartLoading]);

  const hasOutOfStock = useMemo(() => {
    const { hasOutOfStock } = checkCartItemsStock(productList);

    return hasOutOfStock;
  }, [productList]);

  const closeMiniCart = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const { resolveCartKeyChains } = useResolveCartKeyChains();

  const handleRemoveItem = async id => {
    try {
      const removeItemRet = await removeItem({
        variables: {
          cartId,
          itemId: id,
        },
      });
      resolveCartKeyChains({ cartItems: removeItemRet?.data?.removeItemFromCart?.cart?.items || [] });

      const [product] = productList.filter(p => (p.uid || p.id) === id);

      const selectedOptionsLabels =
        product.configurable_options?.map(({ option_label, value_label }) => ({
          attribute: option_label,
          value: value_label,
        })) || null;

      dispatch({
        type: 'CART_REMOVE_ITEM',
        payload: {
          cartId,
          sku: product.product.sku,
          name: product.product.name,
          priceTotal: product?.prices.price.value,
          currencyCode: product?.prices.price.currency,
          discountAmount: product?.prices.total_item_discount.value,
          selectedOptions: selectedOptionsLabels,
          quantity: product.quantity,
        },
      });
    } catch (e) {
      // Error is logged by apollo link - no need to double log.
    }
  };

  const handleProceedToCheckout = useCallback(() => {
    setIsOpen(false);
    router.push('/checkout');
  }, [router, setIsOpen]);

  const handleEditCart = useCallback(() => {
    setIsOpen(false);
    router.push('/cart');
  }, [router, setIsOpen]);

  const derivedErrorMessage = useMemo(() => deriveErrorMessage([removeItemError]), [removeItemError]);

  useEffect(() => {
    if (isOpen) {
      dispatch({
        type: 'MINI_CART_VIEW',
        payload: {
          cartId: cartId,
          products: productList,
        },
      });
    }
  }, [isOpen, cartId, productList, dispatch]);

  return {
    closeMiniCart,
    errorMessage: derivedErrorMessage,
    handleEditCart,
    handleProceedToCheckout,
    handleRemoveItem,
    loading: miniCartLoading || (removeItemCalled && removeItemLoading),
    miniCartLoading,
    productList,
    subTotal,
    totalQuantity,
    configurableThumbnailSource,
    storeUrlSuffix,
    updatingUids,
    handleUpdateItemQuantity,
    hasOutOfStock,
  };
};
